import { useEffect, useState } from "react";
import { RadioGroup } from "@headlessui/react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import {
  CurrencyDollarIcon,
  GlobeAmericasIcon,
} from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/20/solid";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductAction } from "../../../redux/slices/products/productSlices";
import {
  addOrderToCartaction,
  getCartItemsFromLocalStorageAction,
} from "../../../redux/slices/cart/cartSlice";

import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import { getOptimizedImage } from '../../../utils/cloudinary';

const policies = [
  {
    nameKey: "internationalDelivery",
    icon: GlobeAmericasIcon,
    descriptionKey: "getOrderIn2Years",
  },
  {
    nameKey: "loyaltyRewards",
    icon: CurrencyDollarIcon,
    descriptionKey: "dontLookAtOtherTees",
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Product() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams(); // ✅ Moved up before usage

  const [selectedSize, setSelectedSize] = useState("");
 
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Scroll to top and reset image loading state when product id changes
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.location.hash) {
      window.history.replaceState(null, null, " ");
    }
    setImagesLoaded(false);
  }, [id]);

  // Fetch product data
  useEffect(() => {
    dispatch(fetchProductAction(id));
  }, [dispatch, id]);

  // Get cart items from localStorage on mount
  useEffect(() => {
    dispatch(getCartItemsFromLocalStorageAction());
  }, [dispatch]);

  // Rename loading to productLoading to avoid conflict
  const {
    loading: productLoading,
    error,
    product: { product },
  } = useSelector((state) => state?.products);
  

  const { cartItems } = useSelector((state) => state?.carts);

  const productExists = cartItems?.find(
    (item) => item?._id?.toString() === product?._id?.toString()
  );

  

  // Track when all images have loaded
  const handleImageLoad = () => {
    if (product?.images?.length) {
      const allLoaded = product.images.every((_, idx) => {
        const img = document.getElementById(`product-img-${idx}`);
        return img?.complete;
      });
      if (allLoaded) setImagesLoaded(true);
    }
  };

  const addToCartHandler = () => {
    if (productExists) {
      return Swal.fire({
        icon: "error",
        title: t("common.error"),
        text: t("product.productAlreadyInCart"),
      });
    }
    // Uncomment when size selection is enforced
    // if (selectedSize === "") {
    //   return Swal.fire({
    //     icon: "error",
    //     title: t("common.error"),
    //     text: t("product.pleaseSelectSize"),
    //   });
    // }
    dispatch(
      addOrderToCartaction({
        _id: product?._id,
        name: product?.name,
        qty: 1,
        price: product?.price,
        description: product?.description,
        style: product?.style,
        //size: selectedSize,
        image: product?.images[0],
        totalPrice: product?.price,
        qtyLeft: product?.qtyLeft,
      })
    );
    Swal.fire({
      icon: "success",
      title: t("common.success", "Success"),
      text: t("product.productAddedToCart"),
    });
    return dispatch(getCartItemsFromLocalStorageAction());
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(t("locale", { lng: "en" }));
  };

  // ✅ NOW place conditional returns
  if (error) {
    return (
      <div className="bg-white">
        <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:max-w-7xl lg:px-8">
          <ErrorMsg message={error?.message || "Failed to load product"} />
          <div className="mt-6 text-center">
            <Link to="/products" className="inline-block rounded-md bg-orange-600 px-6 py-3 text-sm font-medium text-white hover:bg-orange-700">
              {t("product.backToProducts")}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Loading skeleton
  if (productLoading || !product) {
    return (
      <div className="bg-white">
        <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:max-w-7xl lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-4" />
            <div className="h-64 bg-gray-200 rounded mb-6" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <main className="mx-auto mt-8 max-w-2xl px-4 pb-16 sm:px-6 sm:pb-24 lg:max-w-7xl lg:px-8">
        <div className="lg:grid lg:auto-rows-min lg:grid-cols-12 lg:gap-x-8">
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="flex justify-between">
              <h1 className="text-xl font-medium text-gray-900">
                {product?.name}
              </h1>
              <p className="text-xl font-medium text-gray-900">
                $ {product?.price}.00
              </p>
            </div>
            {/* Reviews */}
            <div className="mt-4">
              <h2 className="sr-only">{t("product.reviews")}</h2>
              <div className="flex items-center">
                  <p className="text-sm text-gray-700">
                  {product?.reviews?.length > 0 && product?.averageRating != null
                    ? Number(product.averageRating).toFixed(1)
                    : "0.0"}
                </p>
                <div className="ml-1 flex items-center">
                  {[0, 1, 2, 3, 4].map((rating) => (
                    <StarIcon
                      key={rating}
                      className={classNames(
                        +product?.averageRating > rating
                          ? "text-yellow-400"
                          : "text-gray-200",
                        "h-5 w-5 flex-shrink-0"
                      )}
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <div
                  aria-hidden="true"
                  className="ml-4 text-sm text-gray-300"
                ></div>
                <div className="ml-4 flex">
                  <a
                    href="#reviews-heading"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('reviews-heading')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    {product?.reviews?.length} {t("product.totalReviews")}
                  </a>
                </div>
              </div>
              <div className="mt-4">
                <Link to={`/add-review/${product?._id}`}>
                  <h3 className="text-sm font-medium text-blue-600">
                    {t("product.leaveReview")}
                  </h3>
                </Link>
              </div>
            </div>
          </div>

          {/* Image gallery with skeleton/fade */}
          <div className="mt-4 lg:col-span-7 lg:col-start-1 lg:row-span-3 lg:row-start-1 lg:mt-0">
            <div
              key={product?._id} // Force remount on product change
              className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-2 lg:gap-4"
            >
              {product?.images?.map((imageUrl, index) => (
                <div
                  key={index}
                  className={`relative overflow-hidden rounded-lg bg-gray-100 ${
                    index === 0
                      ? "lg:col-span-2 lg:row-span-2"
                      : "lg:block"
                  }`}
                >
                  <div
                    className={`absolute inset-0 bg-gray-200 animate-pulse transition-opacity duration-300 ${
                      imagesLoaded ? "opacity-0" : "opacity-100"
                    }`}
                  />
                    <img
                      id={`product-img-${index}`}
                      src={getOptimizedImage(imageUrl, {
                        width: index === 0 ? 800 : 400,
                        height: index === 0 ? 800 : 400,
                        quality: 'auto',
                        format: 'auto',
                      })}
                      alt={`${t("product.productImage")} ${index + 1}`}
                      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
                        imagesLoaded ? "opacity-100" : "opacity-0"
                      }`}
                      loading="lazy"
                      onLoad={handleImageLoad}
                      onError={() => setImagesLoaded(true)}
                    />
                  <div className="pb-[100%]" />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 lg:col-span-5">
            <>
              {/* Size picker */}
              {product?.sizes && product.sizes.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-medium text-gray-900">
                      {t("product.size")}
                    </h2>
                  </div>
                  <RadioGroup
                    value={selectedSize}
                    onChange={setSelectedSize}
                    className="mt-2"
                  >
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
                      {(() => {
                        const sizes = product?.sizes;
                        if (!sizes) return null;
                        const sizeArray = Array.isArray(sizes)
                          ? sizes
                          : sizes.split(",").map((s) => s.trim()).filter(Boolean);
                        return sizeArray.map((size) => (
                          <RadioGroup.Option
                            key={size}
                            value={size}
                            className={({ active, checked }) =>
                              classNames(
                                checked
                                  ? "bg-orange-600 border-transparent text-white hover:bg-orange-700"
                                  : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50",
                                "border rounded-md py-3 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1 cursor-pointer"
                              )
                            }
                          >
                            <RadioGroup.Label as="span">{size}</RadioGroup.Label>
                          </RadioGroup.Option>
                        ));
                      })()}
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* add to cart */}
              {product?.qtyLeft <= 0 ? (
                <button
                  style={{ cursor: "not-allowed" }}
                  disabled
                  className="mt-8 flex w-full items-center justify-center rounded-md border border-transparent bg-gray-600 py-3 px-8 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  {t("product.outOfStock")}
                </button>
              ) : (
                <button
                  onClick={() => addToCartHandler()}
                  className="mt-8 flex w-full items-center justify-center rounded-md border border-transparent bg-orange-600 py-3 px-8 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  {t("product.addToCart")}
                </button>
              )}

              {/* proceed to check */}
              {cartItems.length > 0 && (
                <Link
                  to="/shopping-cart"
                  className="mt-8 flex w-full items-center justify-center rounded-md border border-transparent bg-green-700 py-3 px-8 text-base font-medium text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  {t("product.proceedToCheckout")}
                </Link>
              )}
            </>

            {/* Product details */}
            <div className="mt-10">
              <h2 className="text-sm font-medium text-gray-900">
                {t("product.description")}
              </h2>
              <div className="prose prose-sm mt-4 text-gray-500">
                {product?.description}
              </div>
            </div>

            {/* Policies */}
            <section aria-labelledby="policies-heading" className="mt-10">
              <h2 id="policies-heading" className="sr-only">
                {t("product.ourPolicies")}
              </h2>
              <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {policies.map((policy) => (
                  <div
                    key={policy.nameKey}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center"
                  >
                    <dt>
                      <policy.icon
                        className="mx-auto h-6 w-6 flex-shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                      <span className="mt-4 text-sm font-medium text-gray-900">
                        {t(`product.${policy.nameKey}`)}
                      </span>
                    </dt>
                    <dd className="mt-1 text-sm text-gray-500">
                      {t(`product.${policy.descriptionKey}`)}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>
        </div>

        {/* Reviews */}
        <section aria-labelledby="reviews-heading" className="mt-16 sm:mt-24">
          <h2 id="reviews-heading" className="text-lg font-medium text-gray-900">
            {t("product.recentReviews")}
          </h2>
          <div className="mt-6 space-y-10 divide-y divide-gray-200 border-t border-b border-gray-200 pb-10">
            {product?.reviews.map((review) => (
              <div
                key={review._id}
                className="pt-10 lg:grid lg:grid-cols-12 lg:gap-x-8"
              >
                <div className="lg:col-span-8 lg:col-start-5 xl:col-span-9 xl:col-start-4 xl:grid xl:grid-cols-3 xl:items-start xl:gap-x-8">
                  <div className="flex items-center xl:col-span-1">
                    <div className="flex items-center">
                      {[0, 1, 2, 3, 4].map((rating) => (
                        <StarIcon
                          key={rating}
                          className={classNames(
                            review.rating > rating
                              ? "text-yellow-400"
                              : "text-gray-200",
                            "h-5 w-5 flex-shrink-0"
                          )}
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                    <p className="ml-3 text-sm text-gray-700">
                      {review.rating}
                      <span className="sr-only"> {t("product.outOfStars")}</span>
                    </p>
                  </div>
                  <div className="mt-4 lg:mt-6 xl:col-span-2 xl:mt-0">
                    <h3 className="text-sm font-medium text-gray-900">
                      {review?.message}
                    </h3>
                  </div>
                </div>
                <div className="mt-6 flex items-center text-sm lg:col-span-4 lg:col-start-1 lg:row-start-1 lg:mt-0 lg:flex-col lg:items-start xl:col-span-3">
                  <p className="font-medium text-gray-900">
                    {review.user?.fullname}
                  </p>
                  <time
                    dateTime={review.datetime}
                    className="ml-4 border-l border-gray-200 pl-4 text-gray-500 lg:ml-0 lg:mt-2 lg:border-0 lg:pl-0"
                  >
                    {formatDate(review.createdAt)}
                  </time>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}